import React, { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, Box, Typography, Stack } from "@mui/material";
import { contestantDisplayName } from "../../../utils/contestantDisplayName";
import { formatTotalScore } from "../../../utils/finalNightScoring";
import {
  MISS_GENERATION_DETAIL_SURFACE,
  MISS_GENERATION_PODIUM_BG_POSITION,
} from "../../../utils/missGenerationCompetition";
import MissGenerationBackgroundOverlay, {
  PODIUM_DARK_SCRIM,
} from "./MissGenerationBackgroundOverlay";

function useCountUp(target, duration = 1200, delay = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let rafId;
    let startTime = null;
    const tid = setTimeout(() => {
      const step = (ts) => {
        if (!startTime) startTime = ts;
        const p = Math.min((ts - startTime) / duration, 1);
        setValue(Math.floor(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) rafId = requestAnimationFrame(step);
        else setValue(target);
      };
      rafId = requestAnimationFrame(step);
    }, delay);
    return () => {
      clearTimeout(tid);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [target, duration, delay]);
  return value;
}

/** Soft champagne sparkle — elegant, not arcade confetti */
function spawnSparkles({
  canvas,
  containerRef,
  count = 90,
  originX,
  originY,
  speed = [1.2, 4.5],
  decay = [0.008, 0.018],
}) {
  const parent = containerRef?.current;
  const W = parent ? parent.offsetWidth : 700;
  const H = parent ? parent.offsetHeight : 300;
  canvas.width = W;
  canvas.height = H;
  const cx = originX ?? W / 2;
  const cy = originY ?? H * 0.38;
  const COLORS = [
    "#F6E7C1",
    "#E8D5B5",
    "#D4AF37",
    "#FFF8E7",
    "#FFFFFF",
    "#EF364E",
    "#F5C6CB",
  ];
  return Array.from({ length: count }, () => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.2;
    const spd = speed[0] + Math.random() * (speed[1] - speed[0]);
    const sz = 1.2 + Math.random() * 2.8;
    return {
      x: cx + (Math.random() - 0.5) * 40,
      y: cy,
      vx: Math.cos(angle) * spd * 0.55,
      vy: Math.sin(angle) * spd - Math.random() * 1.2,
      size: sz,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      alpha: 0.9,
      decay: decay[0] + Math.random() * (decay[1] - decay[0]),
      rotate: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.12,
    };
  });
}

const SparkleCanvas = ({ trigger, containerRef, hovered }) => {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    particles: [],
    rafId: null,
    running: false,
    hovering: false,
  });

  const startLoop = useCallback(() => {
    const s = stateRef.current;
    if (s.running) return;
    s.running = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const loop = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      s.particles = s.particles.filter((p) => p.alpha > 0);
      s.particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.04;
        p.vx *= 0.99;
        p.alpha -= p.decay;
        p.rotate += p.rotSpeed;
        ctx.save();
        ctx.globalAlpha = Math.max(p.alpha, 0);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 4;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotate);
        ctx.beginPath();
        ctx.arc(0, 0, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
      if (s.particles.length > 0 || s.hovering) {
        s.rafId = requestAnimationFrame(loop);
      } else {
        s.running = false;
        ctx.clearRect(0, 0, W, H);
      }
    };
    s.rafId = requestAnimationFrame(loop);
  }, []);

  useEffect(() => {
    if (!trigger || !canvasRef.current) return;
    const parent = containerRef?.current;
    const W = parent?.offsetWidth || 700;
    const H = parent?.offsetHeight || 300;
    const burst = spawnSparkles({
      canvas: canvasRef.current,
      containerRef,
      count: 110,
      originX: W / 2,
      originY: H * 0.36,
      speed: [1.5, 5.5],
    });
    stateRef.current.particles.push(...burst);
    startLoop();
  }, [trigger, containerRef, startLoop]);

  useEffect(() => {
    const s = stateRef.current;
    s.hovering = hovered;
    if (!hovered) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const interval = setInterval(() => {
      const W = canvas.width || containerRef?.current?.offsetWidth || 700;
      const H = canvas.height || containerRef?.current?.offsetHeight || 300;
      const drizzle = spawnSparkles({
        canvas,
        containerRef,
        count: 8,
        originX: W * 0.35 + Math.random() * W * 0.3,
        originY: H * 0.2 + Math.random() * H * 0.25,
        speed: [0.6, 2.2],
        decay: [0.012, 0.022],
      });
      s.particles.push(...drizzle);
      startLoop();
    }, 140);
    return () => {
      clearInterval(interval);
      s.hovering = false;
    };
  }, [hovered, containerRef, startLoop]);

  useEffect(
    () => () => {
      const s = stateRef.current;
      if (s.rafId) cancelAnimationFrame(s.rafId);
    },
    []
  );

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 12,
      }}
    />
  );
};

const AuraRings = ({ visible }) => {
  const rings = [
    { s: 128, color: "rgba(240,199,94,0.55)", dur: 3.2, delay: 0, scale: 1.55 },
    { s: 128, color: "rgba(239,54,78,0.28)", dur: 3.6, delay: 0.7, scale: 1.85 },
    { s: 128, color: "rgba(246,231,193,0.35)", dur: 4.2, delay: 1.2, scale: 2.15 },
  ];
  return (
    <>
      {rings.map((r, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: "48%",
            left: "50%",
            width: r.s,
            height: r.s,
            mt: `-${r.s / 2}px`,
            ml: `-${r.s / 2}px`,
            borderRadius: "50%",
            border: `1px solid ${r.color}`,
            pointerEvents: "none",
            zIndex: 1,
            opacity: visible ? 1 : 0,
            animation: visible
              ? `pageantAura_${i} ${r.dur}s ease-out ${r.delay}s infinite`
              : "none",
            [`@keyframes pageantAura_${i}`]: {
              "0%": { transform: "scale(1)", opacity: 0.75 },
              "100%": { transform: `scale(${r.scale})`, opacity: 0 },
            },
          }}
        />
      ))}
      {visible && (
        <Box
          sx={{
            position: "absolute",
            top: "48%",
            left: "50%",
            width: 160,
            height: 160,
            mt: "-80px",
            ml: "-80px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(239,54,78,0.2) 0%, rgba(240,199,94,0.16) 40%, transparent 70%)",
            pointerEvents: "none",
            zIndex: 0,
            animation: "pageantGlowBreath 3.4s ease-in-out infinite",
            "@keyframes pageantGlowBreath": {
              "0%,100%": { transform: "scale(1)", opacity: 0.75 },
              "50%": { transform: "scale(1.14)", opacity: 1 },
            },
          }}
        />
      )}
    </>
  );
};

/** Soft stage light behind 1st place */
const StageLight = ({ visible }) => (
  <Box
    sx={{
      position: "absolute",
      top: "-6%",
      left: "50%",
      width: 220,
      height: 260,
      ml: "-110px",
      pointerEvents: "none",
      zIndex: 0,
      opacity: visible ? 1 : 0,
      transition: "opacity 0.9s ease",
      background:
        "linear-gradient(180deg, rgba(239,54,78,0.22) 0%, rgba(240,199,94,0.18) 28%, rgba(246,231,193,0.08) 55%, transparent 82%)",
      clipPath: "polygon(38% 0%, 62% 0%, 92% 100%, 8% 100%)",
      filter: "blur(1px)",
      animation: visible ? "stageSway 5.5s ease-in-out infinite" : "none",
      "@keyframes stageSway": {
        "0%,100%": { opacity: 0.7, transform: "scaleX(1)" },
        "50%": { opacity: 1, transform: "scaleX(1.05)" },
      },
    }}
  />
);

/** Compact podium (legacy / narrow embeds). */
const RANK_META = {
  1: {
    label: "First",
    accent: "#F0C75E",
    glow: "rgba(239,54,78,0.35)",
    cardW: 168,
    cardH: 214,
    lift: -22,
    pedestalH: 22,
    pedestalW: 148,
  },
  2: {
    label: "Second",
    accent: "#E8C4A8",
    glow: "rgba(232,196,168,0.28)",
    cardW: 140,
    cardH: 178,
    lift: 4,
    pedestalH: 14,
    pedestalW: 128,
  },
  3: {
    label: "Third",
    accent: "#C9956C",
    glow: "rgba(201,149,108,0.25)",
    cardW: 128,
    cardH: 164,
    lift: 12,
    pedestalH: 10,
    pedestalW: 116,
  },
};

/** Default leaderboard / fashion showcase — fills available width on desktop. */
const RANK_META_LARGE = {
  1: {
    ...RANK_META[1],
    cardW: 248,
    cardH: 322,
    cardWMd: 292,
    cardHMd: 380,
    pedestalH: 28,
    pedestalW: 210,
    pedestalWMd: 252,
    lift: -28,
  },
  2: {
    ...RANK_META[2],
    cardW: 208,
    cardH: 272,
    cardWMd: 248,
    cardHMd: 324,
    pedestalH: 20,
    pedestalW: 182,
    pedestalWMd: 218,
    lift: 0,
  },
  3: {
    ...RANK_META[3],
    cardW: 188,
    cardH: 246,
    cardWMd: 228,
    cardHMd: 298,
    pedestalH: 16,
    pedestalW: 166,
    pedestalWMd: 198,
    lift: 8,
  },
};

const ANIM_DELAY = { 1: 620, 2: 160, 3: 360 };

function displayName(row) {
  return contestantDisplayName(row);
}

function subtitleOf(row) {
  if (row?.videoCount > 1) return `${row.videoCount} uploads`;
  const event = row?.event?.name;
  const cat = row?.category;
  if (event && cat) return `${event} · ${cat}`;
  return event || cat || "Contestant";
}

const PodiumItem = ({
  row,
  rank,
  winner,
  finalNightScoring = false,
  largePodium = false,
  lightSurface = false,
  flatPedestal = false,
}) => {
  const [visible, setVisible] = useState(false);
  const delay = ANIM_DELAY[rank] ?? 300;
  const metaBase = largePodium ? RANK_META_LARGE : RANK_META;
  const meta = metaBase[rank] || metaBase[3];
  const voteCount = row?.voteCount || row?.vote_count || 0;
  const votes = useCountUp(voteCount, winner ? 1300 : 1100, delay + 280);
  const judgeAvg = Number(row?.judge_avg) || 0;
  const totalScore = formatTotalScore(row?.total_score ?? voteCount);
  const cover = row?.contestant?.profile_image || row?.contestant?.user?.profile_img || row?.thumbnail;
  const [imgFailed, setImgFailed] = useState(false);
  const showPhoto = Boolean(cover) && !imgFailed;

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <Box
      sx={{
        textAlign: "center",
        transform: `translateY(${meta.lift}px)`,
        position: "relative",
        flex: winner ? 1.12 : 1,
        maxWidth: winner
          ? largePodium
            ? { xs: 176, sm: 340, md: 420 }
            : { xs: 150, sm: 260 }
          : largePodium
            ? { xs: 152, sm: 300, md: 360 }
            : { xs: 120, sm: 220 },
        minWidth: 0,
        zIndex: winner ? 8 : 3,
        overflow: "visible",
      }}
    >
      {winner && !lightSurface && !flatPedestal ? (
        <StageLight visible={visible} />
      ) : null}
      {winner && !lightSurface && !flatPedestal ? (
        <AuraRings visible={visible} />
      ) : null}

      {winner && (
        <Box
          sx={{
            mb: 0.8,
            position: "relative",
            zIndex: 6,
            opacity: visible ? 1 : 0,
            transform: visible
              ? "translateY(0) scale(1)"
              : "translateY(-18px) scale(0.6)",
            transition:
              "opacity 0.55s ease, transform 0.7s cubic-bezier(0.22,1,0.36,1)",
            transitionDelay: "0.35s",
            animation: visible
              ? "pageantTiaraFloat 3.2s ease-in-out 1.1s infinite"
              : "none",
            "@keyframes pageantTiaraFloat": {
              "0%,100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-5px)" },
            },
            filter:
              "drop-shadow(0 2px 12px rgba(240,199,94,0.85)) drop-shadow(0 0 8px rgba(212,175,55,0.55))",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 28, sm: 40, md: 48 },
              lineHeight: 1,
              letterSpacing: "0.02em",
              color: "#F0C75E",
              textShadow:
                "0 0 18px rgba(240,199,94,0.65), 0 2px 0 #C47A2A",
            }}
            aria-hidden
          >
            ♛
          </Typography>
        </Box>
      )}

      {/* Portrait card — profile only (no video on podium) */}
      <Box
        sx={{
          position: "relative",
          width: {
            xs: winner ? (largePodium ? 156 : 104) : largePodium ? 132 : 86,
            sm: meta.cardW,
            md: meta.cardWMd ?? meta.cardW,
          },
          height: {
            xs: winner ? (largePodium ? 200 : 132) : largePodium ? 168 : 110,
            sm: meta.cardH,
            md: meta.cardHMd ?? meta.cardH,
          },
          mx: "auto",
          zIndex: 5,
          borderRadius: 2.5,
          overflow: "hidden",
          border: `2.5px solid ${meta.accent}`,
          boxShadow: winner
            ? "0 14px 36px rgba(0,0,0,0.5), 0 0 0 3px rgba(212,175,55,0.18)"
            : "0 10px 26px rgba(0,0,0,0.4)",
          opacity: visible ? 1 : 0,
          transform: visible ? "scale(1)" : "scale(0.2)",
          transition:
            "opacity 0.5s ease, transform 0.75s cubic-bezier(0.22,1,0.36,1)",
          bgcolor: "#11131c",
        }}
      >
        {showPhoto ? (
          <Box
            component="img"
            src={cover}
            alt=""
            onError={() => setImgFailed(true)}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#1b1e36",
            }}
          >
            <Avatar
              sx={{
                width: winner ? { xs: 72, sm: 112 } : { xs: 56, sm: 88 },
                height: winner ? { xs: 72, sm: 112 } : { xs: 56, sm: 88 },
                bgcolor: "#bdbdbd",
                color: "#fff",
              }}
            />
          </Box>
        )}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            transform: "none",
            width: winner ? 32 : 28,
            height: winner ? 32 : 28,
            borderRadius: "50%",
            bgcolor: "#161922",
            color: meta.accent,
            border: `1.5px solid ${meta.accent}`,
            fontWeight: 800,
            fontFamily: "Montserrat, sans-serif",
            fontSize: winner ? 13 : 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 7,
          }}
        >
          {rank}
        </Box>
      </Box>

      {/* Stage pedestal with soft shimmer for 1st */}
      <Box
        sx={{
          mx: "auto",
          mt: 1.5,
          width: {
            xs: winner ? 108 : 88,
            sm: meta.pedestalW,
            md: meta.pedestalWMd ?? meta.pedestalW,
          },
          height: meta.pedestalH,
          borderRadius: "3px 3px 2px 2px",
          ...(flatPedestal
            ? {
                bgcolor: winner ? "#F0C75E" : meta.accent,
                boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
              }
            : {
                background: winner
                  ? "linear-gradient(180deg, #FFE08A 0%, #F0C75E 40%, #E8A838 70%, #C47A2A 100%)"
                  : `linear-gradient(180deg, ${meta.accent} 0%, ${meta.accent}99 100%)`,
                boxShadow: winner
                  ? "0 6px 18px rgba(239,54,78,0.22), 0 4px 14px rgba(240,199,94,0.35), inset 0 1px 0 rgba(255,255,255,0.4)"
                  : `0 4px 12px ${meta.glow || "rgba(0,0,0,0.2)"}, inset 0 1px 0 rgba(255,255,255,0.2)`,
                overflow: "hidden",
                "&::after": winner
                  ? {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.4) 50%, transparent 65%)",
                      backgroundSize: "220% 100%",
                      animation: "pedestalSheen 3.4s ease-in-out infinite",
                      "@keyframes pedestalSheen": {
                        "0%": { backgroundPosition: "120% 0" },
                        "100%": { backgroundPosition: "-120% 0" },
                      },
                    }
                  : undefined,
              }),
          opacity: visible ? 1 : 0,
          transform: visible ? "scaleX(1)" : "scaleX(0.25)",
          transition:
            "opacity 0.45s ease, transform 0.55s cubic-bezier(0.22,1,0.36,1)",
          transitionDelay: "0.25s",
          position: "relative",
          zIndex: 4,
        }}
      />

      <Typography
        sx={{
          mt: 1.4,
          color: lightSurface ? "#222222" : "#fff",
          fontWeight: 700,
          fontFamily: "Montserrat, sans-serif",
          fontSize: winner ? { xs: 12, sm: 15 } : { xs: 11, sm: 13 },
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(10px)",
          transition: "opacity 0.45s ease, transform 0.45s ease",
          transitionDelay: "0.3s",
          px: 0.5,
          lineHeight: 1.25,
        }}
      >
        {displayName(row)}
      </Typography>
      <Typography
        sx={{
          color: lightSurface ? "#666666" : "rgba(255,255,255,0.48)",
          fontSize: 11,
          fontWeight: 500,
          mt: 0.35,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.4s ease",
          transitionDelay: "0.35s",
        }}
      >
        {subtitleOf(row)}
      </Typography>

      {finalNightScoring ? (
        <Box
          sx={{
            mt: 1,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(8px)",
            transition: "opacity 0.4s ease, transform 0.4s ease",
            transitionDelay: "0.4s",
          }}
        >
          <Typography
            sx={{
              color: meta.accent,
              fontWeight: 800,
              fontFamily: "Montserrat, sans-serif",
              fontSize: winner ? { xs: 16, sm: 20 } : { xs: 14, sm: 16 },
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {totalScore}
          </Typography>
          <Typography
            sx={{
              color: lightSurface ? "#888888" : "rgba(255,255,255,0.5)",
              fontSize: 10,
              fontWeight: 600,
              mt: 0.45,
              lineHeight: 1.35,
            }}
          >
            {judgeAvg > 0 ? `Judge ${judgeAvg}/10` : "Awaiting judge scores"}
            {" · "}
            {votes} vote{voteCount === 1 ? "" : "s"}
          </Typography>
        </Box>
      ) : (
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="center"
          gap={0.55}
          sx={{
            mt: 1,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(8px)",
            transition: "opacity 0.4s ease, transform 0.4s ease",
            transitionDelay: "0.4s",
          }}
        >
          <Typography
            sx={{
              color: meta.accent,
              fontWeight: 800,
              fontFamily: "Montserrat, sans-serif",
              fontSize: winner ? { xs: 16, sm: 20 } : { xs: 14, sm: 16 },
              letterSpacing: "-0.02em",
            }}
          >
            {votes}
          </Typography>
          <Typography
            sx={{
              color: lightSurface ? "#888888" : "rgba(255,255,255,0.42)",
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            votes
          </Typography>
        </Stack>
      )}

      <Typography
        sx={{
          mt: 0.7,
          color: meta.accent,
          fontWeight: 700,
          letterSpacing: "0.14em",
          fontSize: 10,
          textTransform: "uppercase",
          opacity: visible ? 0.9 : 0,
          transition: "opacity 0.4s ease",
          transitionDelay: "0.45s",
        }}
      >
        {meta.label}
      </Typography>
    </Box>
  );
};

/**
 * Contestant podium — ranks by total votes. Profile cards only (no video).
 */
export default function CompetitionPodium({
  rows = [],
  videos,
  categoryTitle = "",
  podiumHeading = "Top contestants",
  largePodium = true,
  finalNightScoring = false,
  judgesPercent = null,
  usersPercent = null,
  backgroundImage = null,
  backgroundImageOpacity = 0.1,
}) {
  const containerRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [burst, setBurst] = useState(false);
  const list = rows?.length ? rows : videos || [];
  const top = list.slice(0, 3);
  const first = top[0];
  const second = top[1];
  const third = top[2];

  useEffect(() => {
    if (!first) return;
    setBurst(false);
    const t = setTimeout(() => setBurst(true), ANIM_DELAY[1] + 380);
    return () => clearTimeout(t);
  }, [first?.id]);

  if (!first) return null;

  const slots = [
    second ? { row: second, rank: 2, winner: false } : null,
    { row: first, rank: 1, winner: true },
    third ? { row: third, rank: 3, winner: false } : null,
  ].filter(Boolean);

  const watermarkedPodium = Boolean(backgroundImage);

  return (
    <Box
      ref={containerRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 3,
        mb: 3,
        py: largePodium ? { xs: 5, sm: 4, md: 5 } : { xs: 4.5, sm: 3.5, md: 4.5 },
        px: { xs: 1.25, sm: 3 },
        ...(watermarkedPodium
          ? {
              bgcolor: MISS_GENERATION_DETAIL_SURFACE,
              border: "1px solid #dddddd",
              boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
            }
          : {
              background: PODIUM_DARK_SCRIM,
              border: "1px solid rgba(240,199,94,0.28)",
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.06), 0 12px 40px rgba(239,54,78,0.08)",
            }),
      }}
    >
      {watermarkedPodium ? (
        <MissGenerationBackgroundOverlay
          imageUrl={backgroundImage}
          variant="light"
          opacity={backgroundImageOpacity}
          backgroundPosition={MISS_GENERATION_PODIUM_BG_POSITION}
        />
      ) : null}
      <SparkleCanvas
        trigger={burst}
        containerRef={containerRef}
        hovered={hovered}
      />
      {categoryTitle ? (
        <Box
          sx={{
            position: "absolute",
            top: { xs: 10, sm: 16 },
            left: { xs: 10, sm: 16 },
            right: { xs: 10, sm: "auto" },
            zIndex: 3,
            maxWidth: { xs: "calc(100% - 20px)", sm: "72%" },
            px: { xs: 1.1, sm: 1.5 },
            py: { xs: 0.7, sm: 0.85 },
            borderRadius: 1.5,
            bgcolor: watermarkedPodium
              ? "rgba(255,255,255,0.92)"
              : "rgba(12,14,22,0.72)",
            border: watermarkedPodium
              ? "1px solid #807154"
              : "1px solid rgba(255,205,31,0.5)",
            color: watermarkedPodium ? "#807154" : "#FFCD1F",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 800,
            fontSize: { xs: 12, sm: 14, md: 15 },
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            lineHeight: 1.3,
            whiteSpace: "normal",
            wordBreak: "break-word",
            textShadow: watermarkedPodium
              ? "none"
              : "0 2px 10px rgba(0,0,0,0.65)",
          }}
        >
          {categoryTitle}
        </Box>
      ) : null}
      <Typography
        sx={{
          textAlign: "center",
          color: watermarkedPodium ? "#807154" : "#F6E7C1",
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 700,
          letterSpacing: "0.2em",
          fontSize: 11,
          mb: 3,
          mt: categoryTitle ? { xs: 4.5, sm: 3.5 } : 0,
          textTransform: "uppercase",
          position: "relative",
          zIndex: 2,
          textShadow: watermarkedPodium
            ? "none"
            : "0 0 20px rgba(239,54,78,0.25)",
        }}
      >
        {podiumHeading}
      </Typography>
      {!watermarkedPodium ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: "8%",
            right: "8%",
            bottom: "12%",
            height: "38%",
            pointerEvents: "none",
            zIndex: 1,
            background:
              "radial-gradient(ellipse at 50% 100%, rgba(239,54,78,0.18) 0%, rgba(240,199,94,0.1) 35%, transparent 70%)",
            filter: "blur(2px)",
          }}
        />
      ) : null}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          gap: { xs: 1, sm: 2.5, md: 4 },
          position: "relative",
          zIndex: 2,
          minHeight: largePodium
            ? { xs: 280, sm: 360, md: 420 }
            : { xs: 210, sm: 250 },
          px: { md: 1 },
          overflow: "visible",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {slots.map((s) => (
          <PodiumItem
            key={s.row.id || s.row.contestant_id}
            row={s.row}
            rank={s.rank}
            winner={s.winner}
            finalNightScoring={finalNightScoring}
            largePodium={largePodium}
            lightSurface={watermarkedPodium}
            flatPedestal={watermarkedPodium}
          />
        ))}
      </Box>
    </Box>
  );
}
